import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { ChoiceGroup } from '../../components/Choice';
import { Field, Input } from '../../components/Field';
import { useDb } from '../../db/hooks';
import { addException, removeException, type ScheduleExceptionRow } from '../../data/schedule';
import { optionalText, optionalTime } from '../../lib/forms';
import { localToday } from '../sessions/fields';

const KIND_LABELS = { skip: 'No session', moved: 'Different time', extra: 'Extra session' } as const;

const schema = z
  .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose a date.').refine((value) => value >= localToday(), 'Choose today or a later date.'),
    kind: z.enum(['skip', 'moved', 'extra']),
    new_time: optionalTime,
    note: optionalText(120)
  })
  .refine((values) => values.kind === 'skip' || values.new_time != null, { message: 'Enter the time.', path: ['new_time'] });

/** One-off changes to the regular schedule: a skipped day, a moved session, an extra session. */
export function ScheduleChanges({ exceptions, canWrite }: { exceptions: ScheduleExceptionRow[]; canWrite: boolean }) {
  const db = useDb();
  const [adding, setAdding] = useState(false);
  const upcoming = exceptions.filter((exception) => exception.date >= localToday());
  const form = useForm<z.input<typeof schema>, unknown, z.output<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { date: localToday(), kind: 'skip', new_time: '', note: '' }
  });
  const { errors, isSubmitting } = form.formState;
  const kind = form.watch('kind');

  return (
    <div className="space-y-3">
      {upcoming.length ? (
        <ul className="divide-y divide-clinical-line rounded-lg border border-clinical-line">
          {upcoming.map((exception) => (
            <li key={exception.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="flex-1">
                <span className="font-medium">{format(parseISO(exception.date), 'EEE d MMM')}</span>
                {' · '}
                {KIND_LABELS[exception.kind]}
                {exception.new_time ? ` at ${exception.new_time}` : ''}
                {exception.note ? <span className="block text-xs text-clinical-muted">{exception.note}</span> : null}
              </span>
              {canWrite ? (
                <button
                  type="button"
                  aria-label={`Remove change on ${exception.date}`}
                  className="focus-ring rounded p-1 text-clinical-muted hover:text-clinical-danger"
                  onClick={() => void removeException(db, exception.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-clinical-muted">No upcoming changes.</p>
      )}

      {canWrite && !adding ? (
        <Button variant="secondary" className="w-full" onClick={() => setAdding(true)}>
          Change a date
        </Button>
      ) : null}

      {adding ? (
        <form
          noValidate
          className="space-y-3 rounded-lg border border-dashed border-clinical-line p-3"
          onSubmit={form.handleSubmit(async (values) => {
            await addException(db, { ...values, new_time: values.kind === 'skip' ? null : values.new_time });
            form.reset({ date: localToday(), kind: 'skip', new_time: '', note: '' });
            setAdding(false);
          })}
        >
          <Field label="Date" error={errors.date?.message}>
            <Input type="date" {...form.register('date')} />
          </Field>
          <Controller
            control={form.control}
            name="kind"
            render={({ field }) => (
              <ChoiceGroup
                name="exception-kind"
                value={field.value}
                onChange={field.onChange}
                columns={3}
                options={(Object.keys(KIND_LABELS) as (keyof typeof KIND_LABELS)[]).map((value) => ({ value, label: KIND_LABELS[value] }))}
              />
            )}
          />
          {kind !== 'skip' ? (
            <Field label="Time" error={errors.new_time?.message}>
              <Input type="time" {...form.register('new_time')} />
            </Field>
          ) : null}
          <Field label="Note (optional)" error={errors.note?.message}>
            <Input {...form.register('note')} placeholder="e.g. Public holiday" />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              Save change
            </Button>
            <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
